import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import QuestionAPI from '../../api/questions';
import {
  SOURCES_SORT_ORDER,
  QUESTION_SOURCES_SORTBY,
} from '../../routes/dashboard/sources/constants';
import Vue from 'vue';

const QUESTION_STATUSES = {
  pending: 'pending',
  enabled: 'enabled',
  deleted: 'deleted',
};

export const state = {
  records: [],
  countUnresolved: 0,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  meta: {
    currentPage: 1,
    perPage: 50,
    totalEntries: 0,
    sortOrder: SOURCES_SORT_ORDER.desc,
    sortBy: QUESTION_SOURCES_SORTBY.updatedAt,
    filter: '',
  },
};

export const getters = {
  getQuestions($state) {
    return $state.records;
  },
  getCountUnresolved($state) {
    return $state.countUnresolved;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getMeta: $state => {
    return $state.meta;
  },
};

export const actions = {
  get: async ({ commit }, { page, perPage, sortBy, sortOrder, filter }) => {
    commit(types.default.SET_QUESTION_SOURCES_FETCHING_STATUS, true);
    try {
      const {
        data: { resources, meta, count_unresolved },
      } = await QuestionAPI.get(page, perPage, sortBy, sortOrder, filter);
      commit(types.default.SET_QUESTION_SOURCES_FETCHING_STATUS, false);
      commit(types.default.SET_QUESTION_SOURCES, resources);
      commit(
        types.default.SET_QUESTION_SOURCES_COUNT_UNRESOLVED,
        count_unresolved
      );
      commit(types.default.SET_QUESTION_SOURCES_META, {
        currentPage: meta.current_page,
        perPage: meta.per_page,
        totalEntries: meta.total,
        sortBy: sortBy,
        sortOrder: sortOrder,
        filter: filter,
      });
    } catch (error) {
      commit(types.default.SET_QUESTION_SOURCES_FETCHING_STATUS, false);
      throw error;
    }
  },
  create: async ({ commit }, questionParams) => {
    commit(types.default.SET_QUESTION_SOURCES_CREATING_STATUS, true);
    try {
      await QuestionAPI.create(questionParams);
      actions.refresh({ commit });
      commit(types.default.SET_QUESTION_SOURCES_CREATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_QUESTION_SOURCES_CREATING_STATUS, false);
      throw error;
    }
  },
  update: async ({ commit }, questionParams) => {
    commit(types.default.SET_QUESTION_SOURCES_UPDATING_STATUS, true);
    try {
      await QuestionAPI.update(questionParams.id, {
        question: questionParams,
      });
      actions.refresh({ commit });
      commit(types.default.SET_QUESTION_SOURCES_UPDATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_QUESTION_SOURCES_UPDATING_STATUS, false);
      throw new Error(error);
    }
  },
  delete: async ({ commit }, questionParams) => {
    commit(types.default.SET_QUESTION_SOURCES_DELETING_STATUS, true);
    try {
      await QuestionAPI.update(questionParams.id, {
        question: {
          status: 'deleted',
        },
      });
      actions.refresh({ commit });
      commit(types.default.SET_QUESTION_SOURCES_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_QUESTION_SOURCES_DELETING_STATUS, false);
      throw new Error(error);
    }
  },
  toggleEnabled: async ({ commit }, questionParams) => {
    commit(types.default.TOGGLE_QUESTION_ENABLED_STATUS, true);
    const newStatus =
      questionParams.status === QUESTION_STATUSES.enabled
        ? QUESTION_STATUSES.pending
        : QUESTION_STATUSES.enabled;
    try {
      await QuestionAPI.update(questionParams.id, {
        question: {
          status: newStatus,
        },
      });
      actions.refresh({ commit });
      commit(types.default.TOGGLE_QUESTION_ENABLED_STATUS, false);
    } catch (error) {
      commit(types.default.TOGGLE_QUESTION_ENABLED_STATUS, false);
      throw new Error(error);
    }
  },
  refresh: async ({ commit }) => {
    actions.get(
      { commit },
      {
        page: state.meta.currentPage,
        perPage: state.meta.perPage,
        sortBy: state.meta.sortBy,
        sortOrder: state.meta.sortOrder,
        filter: state.meta.filter,
      }
    );
  },
};

export const mutations = {
  [types.default.SET_QUESTION_SOURCES_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },
  [types.default.SET_QUESTION_SOURCES_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },
  [types.default.SET_QUESTION_SOURCES_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },
  [types.default.SET_QUESTION_SOURCES_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },
  [types.default.SET_QUESTION_SOURCES_COUNT_UNRESOLVED]($state, count) {
    $state.countUnresolved = count;
  },

  [types.default.SET_QUESTION_SOURCES]: MutationHelpers.set,
  [types.default.ADD_QUESTION_SOURCES]: MutationHelpers.create,
  [types.default.EDIT_QUESTION_SOURCES]: MutationHelpers.update,
  [types.default.DELETE_QUESTION_SOURCES]: MutationHelpers.destroy,
  [types.default.SET_QUESTION_SOURCES_META]: ($state, data) => {
    const { totalEntries, currentPage, sortBy, sortOrder, filter } = data;
    Vue.set($state.meta, 'totalEntries', totalEntries);
    Vue.set($state.meta, 'currentPage', currentPage);
    Vue.set($state.meta, 'sortOrder', sortOrder);
    Vue.set($state.meta, 'sortBy', sortBy);
    Vue.set($state.meta, 'filter', filter);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
